import { UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

const oidcUserStorageKey = () =>
    `oidc.user:${process.env.REACT_APP_AUTHORITY}:${process.env.REACT_APP_CLIENT_ID}`;

const tokenRefreshBufferSeconds = 300;
let tokenRefreshPromise: Promise<string | undefined> | undefined;
let refreshMonitorId: number | undefined;

export const getStoredAccessToken = (): string | undefined => {
    const authData = window.sessionStorage.getItem(oidcUserStorageKey());
    if (!authData) return undefined;

    try {
        return JSON.parse(authData).access_token;
    } catch {
        return undefined;
    }
};

export const ensureOfflineAccessScope = (scope?: string): string => {
    const scopes = new Set((scope ?? '').split(' ').filter(Boolean));
    scopes.add('offline_access');
    return Array.from(scopes).join(' ');
};

const getBrowserSessionStore = () =>
    typeof window === 'undefined'
        ? undefined
        : new WebStorageStateStore({ store: window.sessionStorage });

export const oidcConfig: UserManagerSettings = {
    authority: process.env.REACT_APP_AUTHORITY ?? '',
    client_id: process.env.REACT_APP_CLIENT_ID ?? '',
    client_secret: process.env.REACT_APP_CLIENT_SECRET ?? '',
    redirect_uri: process.env.REACT_APP_REDIRECT_URI ?? '',
    post_logout_redirect_uri: process.env.REACT_APP_POST_REDIRECT_URI ?? '',
    scope: ensureOfflineAccessScope(process.env.REACT_APP_SCOPES),
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: false,
    monitorSession: false,
    accessTokenExpiringNotificationTimeInSeconds: tokenRefreshBufferSeconds,
    userStore: getBrowserSessionStore(),
};

export const authUserManager = new UserManager(oidcConfig);

const shouldRefreshToken = (expiresAt?: number, bufferSeconds = tokenRefreshBufferSeconds) => {
    if (!expiresAt) return true;
    return expiresAt * 1000 <= Date.now() + bufferSeconds * 1000;
};

export const getFreshAccessToken = async (
    bufferSeconds = tokenRefreshBufferSeconds
): Promise<string | undefined> => {
    const user = await authUserManager.getUser();
    if (!user) return undefined;

    if (!user.expired && !shouldRefreshToken(user.expires_at, bufferSeconds)) {
        return user.access_token;
    }

    if (!tokenRefreshPromise) {
        tokenRefreshPromise = authUserManager
            .signinSilent()
            .then((refreshedUser) => refreshedUser?.access_token)
            .finally(() => {
                tokenRefreshPromise = undefined;
            });
    }

    try {
        return await tokenRefreshPromise;
    } catch (error) {
        if (!user.expired) return user.access_token;
        throw error;
    }
};

export const startTokenRefreshMonitor = () => {
    if (typeof window === 'undefined' || refreshMonitorId !== undefined) return;

    refreshMonitorId = window.setInterval(() => {
        if (document.visibilityState !== 'visible') return;

        getFreshAccessToken().catch(() => {
            // The next authenticated request will surface the session problem to the user.
        });
    }, 60 * 1000);
};

export const isAuthenticationError = (error: unknown): boolean => {
    const maybeApolloError = error as {
        graphQLErrors?: { message?: string; extensions?: { code?: string } }[];
        networkError?: { statusCode?: number; status?: number; message?: string };
        message?: string;
    };

    const status =
        maybeApolloError.networkError?.statusCode ?? maybeApolloError.networkError?.status;

    if (status === 401 || status === 403) return true;

    const message = (
        maybeApolloError.message ??
        maybeApolloError.networkError?.message ??
        ''
    ).toLowerCase();

    if (
        message.includes('login_required') ||
        message.includes('no refresh token') ||
        message.includes('refresh_token') ||
        message.includes('unauthorized') ||
        message.includes('not authenticated')
    ) {
        return true;
    }

    return (
        maybeApolloError.graphQLErrors?.some((graphQlError) => {
            const code = graphQlError.extensions?.code?.toLowerCase();
            const graphQlMessage = graphQlError.message?.toLowerCase() ?? '';
            return (
                code === 'unauthorized' ||
                code === 'forbidden' ||
                code === 'auth_not_authorized' ||
                graphQlMessage.includes('unauthorized') ||
                graphQlMessage.includes('not authorized') ||
                graphQlMessage.includes('not authenticated')
            );
        }) ?? false
    );
};
